import { useEffect, useRef, useState } from "react";
import { notifyIcon } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { STARTER_NODE_UI_ID } from "../ruleBasedFlow.constants";
import { createStarterEdge, createStarterNode, findFirstNode, transformMongoNodesToReactFlow } from "../ruleBasedFlow.helpers";
import * as ruleBasedFlowService from "../ruleBasedFlow.service";

/**
 * Custom hook to load and restore workflow/task data
 * @param {string} context - Context: "workflow" | "task"
 * @param {string} entityId - Workflow ID or Task ID
 * @param {Function} handleStarterClick - Callback for starter node click
 * @param {Function} handleNodeAddClick - Callback for node add click
 * @param {Function} handleDeleteNode - Callback for node deletion
 * @param {Function} handleUpdateNodeData - Callback for updating node data
 * @param {Function} setNodes - Function to update nodes state
 * @param {Function} setEdges - Function to update edges state
 * @param {Function} setFirstNodeId - Function to set first node ID
 * @param {Function} setViewport - Function to set viewport
 * @param {Function} handleAddNodeBetween - Callback for adding node between edges
 * @param {Function} deleteEdge - Function to delete edges
 * @param {Function} handleUpdateNodeDataEmail - Callback for updating email node data
 * @param {boolean} isReadOnly - Whether the flow is read-only (for task context)
 */
export const useWorkflowData = (
  context = "workflow",
  entityId,
  handleStarterClick,
  handleNodeAddClick,
  handleDeleteNode,
  handleUpdateNodeData,
  setNodes,
  setEdges,
  setFirstNodeId,
  setViewport,
  handleAddNodeBetween,
  deleteEdge,
  handleUpdateNodeDataEmail,
  isReadOnly = false
) => {
  const [isLoading, setIsLoading] = useState(true);

  // Use refs to store latest handler values without triggering re-renders
  const handlersRef = useRef({
    handleStarterClick,
    handleNodeAddClick,
    handleDeleteNode,
    handleUpdateNodeData,
    handleUpdateNodeDataEmail,
    handleAddNodeBetween,
    deleteEdge,
    setNodes,
    setEdges,
    setFirstNodeId,
    setViewport,
  });

  // Update refs when handlers change
  useEffect(() => {
    handlersRef.current = {
      handleStarterClick,
      handleNodeAddClick,
      handleDeleteNode,
      handleUpdateNodeData,
      handleUpdateNodeDataEmail,
      handleAddNodeBetween,
      deleteEdge,
      setNodes,
      setEdges,
      setFirstNodeId,
      setViewport,
    };
  }, [
    handleStarterClick,
    handleNodeAddClick,
    handleDeleteNode,
    handleUpdateNodeData,
    handleUpdateNodeDataEmail,
    handleAddNodeBetween,
    deleteEdge,
    setNodes,
    setEdges,
    setFirstNodeId,
    setViewport,
  ]);

  useEffect(() => {
    const loadFlowData = async () => {
      const handlers = handlersRef.current;
      if (!entityId) {
        console.error(`${context === "workflow" ? "WorkflowId" : "TaskId"} is missing`);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Call appropriate API based on context
        let response;
        if (context === "task") {
          response = await ruleBasedFlowService.getTaskNodesAndEdges(entityId);
        } else {
          response = await ruleBasedFlowService.getNodesAndEdges(entityId);
        }

        // Always create UI-only starter node
        const starterNode = createStarterNode(handlers.handleStarterClick);

        if (response && response?.nodes && response?.nodes?.length > 0) {
          const transformedNodes = transformMongoNodesToReactFlow(
            response.nodes,
            handlers.handleNodeAddClick,
            handlers.handleDeleteNode,
            handlers.handleUpdateNodeDataEmail,
            handlers.handleUpdateNodeData
          );

          // Nodes are not read-only in either context
          const nodesWithReadOnly = transformedNodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
              isReadOnly: isReadOnly,
            },
          }));

          // Find first node (has isFirstNode flag)
          // The true first node should have no incoming edges from backend nodes
          // (starter edge is UI-only, so it won't be in backend edges)
          let firstNode = findFirstNode(response.nodes);
          
          // If we have edges, verify the first node is correct
          // The true first node should have NO incoming edges (since starter edge is UI-only)
          if (firstNode && response.edges && response.edges.length > 0) {
            // Get all nodes that have incoming edges from backend
            const nodesWithIncomingEdges = new Set(
              response.edges.map((edge) => edge.target)
            );
            
            // If the found first node has incoming edges, it's not the true first node
            // Find the node with isFirstNode: true that has NO incoming edges
            if (nodesWithIncomingEdges.has(firstNode._id)) {
              const trueFirstNode = response.nodes.find(
                (node) => node.isFirstNode && !nodesWithIncomingEdges.has(node._id)
              );
              
              if (trueFirstNode) {
                firstNode = trueFirstNode;
              }
            }
          }
          
          if (firstNode) {
            handlers.setFirstNodeId(firstNode._id);
          }

          // Set nodes: starter + all backend nodes
          handlers.setNodes([starterNode, ...nodesWithReadOnly]);

          // Filter out any existing starter edges from backend edges (they shouldn't be there, but just in case)
          const backendEdges = (response.edges || [])
            .filter((edge) => !edge.source || edge.source !== STARTER_NODE_UI_ID)
            .map((edge) => ({
              ...edge,
              id: edge.id || edge._id,
              type: edge.type || "interactive",
              data: {
                ...edge.data,
                onAddNode: isReadOnly ? null : handlers.handleAddNodeBetween,
                onDeleteEdge: isReadOnly ? null : handlers.deleteEdge,
                sourceHandle: edge.sourceHandle,
                isReadOnly: isReadOnly,
              },
            }));

          // Create UI-only edge from starter to first node with handlers
          // Only create if we have a first node
          const uiEdges = firstNode
            ? [
                {
                  ...createStarterEdge(firstNode._id),
                  data: {
                    onAddNode: isReadOnly ? null : handlers.handleAddNodeBetween,
                    onDeleteEdge: isReadOnly ? null : handlers.deleteEdge,
                    sourceHandle: "bottom",
                    isReadOnly: isReadOnly,
                  },
                },
              ]
            : [];

          // Combine UI edges and backend edges
          handlers.setEdges([...uiEdges, ...backendEdges]);
        } else {
          // No nodes exist, show only starter node
          handlers.setNodes([starterNode]);
          handlers.setEdges([]);
        }

        setIsLoading(false);
      } catch (error) {
        console.error(`Error loading ${context} data:`, error);
        getNotification(`Failed to load ${context === "workflow" ? "workflow" : "task"} flow`, notifyIcon.ERROR_ICON);
        // Fallback: show only starter node
        const handlers = handlersRef.current;
        handlers.setNodes([createStarterNode(handlers.handleStarterClick)]);
        handlers.setEdges([]);
        setIsLoading(false);
      }
    };

    loadFlowData();
    // Only reload when context or entityId changes - handlers are captured via refs
  }, [context, entityId, isReadOnly]);

  return { isLoading };
};
